// Small hook that loads a CMS page (`/api/page/{slug}`) and exposes:
//  - loading state
//  - `enabled` (true when state=live AND sections exist)
//  - `sections` array (already ordered + visible)
//  - `seo` map ({title:{en,da,es}, description:{en,da,es}})
//
// Language switching does NOT re-fetch — the payload contains all three
// languages so the render layer picks the right one via `pickTx`. This
// prevents the fallback flash reported in production when users switched
// between EN/DA/ES.
//
// Frontend pages call `useCmsPage("mission")` and use `state.loading` to
// avoid rendering the hardcoded legacy fallback until the CMS payload has
// resolved (or definitely 404'd).
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export function useCmsPage(slug, { preview = false } = {}) {
  const [state, setState] = useState({ loading: true, enabled: false, sections: [], seo: null, raw: null, error: false });
  useEffect(() => {
    let cancelled = false;
    const url = preview ? `/admin/page/${slug}/preview` : `/page/${slug}`;
    api.get(url).then(r => {
      if (cancelled) return;
      const d = r.data || {};
      setState({
        loading: false,
        enabled: !!d.enabled && Array.isArray(d.sections) && d.sections.length > 0,
        sections: (d.sections || []).map(s => ({ id: s.id, ...(s.data || {}) })),
        seo: d.seo || null,
        raw: d,
        error: false,
      });
    }).catch(() => {
      if (cancelled) return;
      // On network failure, keep whatever we had and expose an `error`
      // flag so pages can show a discreet "retry" without wiping the CMS
      // content that was already rendered.
      setState(s => ({ ...s, loading: false, error: true }));
    });
    return () => { cancelled = true; };
  }, [slug, preview]);
  return state;
}

// Pick a value from a {en,da,es} bag with sensible fallbacks.
export function pickTx(bag, lang) {
  if (!bag) return "";
  if (typeof bag === "string") return bag;
  return (bag[lang] || bag.en || bag.da || bag.es || "").toString();
}

// Convert a body string into an array of paragraphs (every CMS line break).
export function paragraphs(text) {
  if (!text) return [];
  return String(text).replace(/\r\n?/g, "\n").split(/\n+/).map(p => p.trim()).filter(Boolean);
}
